// Noise Lab → Models: every trained version side by side (how each did on recordings it never heard),
// and a drop zone to add a new version's folder (model.json + its .npz files, from export_model.py).
import { useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, upload } from "../api/client";
import type { LabModel } from "../api/types";
import { useMe } from "../lib/me";
import { filesFromDrop, visible } from "../lib/folderDrop";
import { LabTabs } from "../components/LabTabs";

const BASE = "/api/modules/noise_lab";

export const pct = (v?: number | null) => (v == null ? "—" : `${Math.round(v * 100)}%`);
export const STATUS_LABEL: Record<string, string> = { live: "Live", candidate: "Candidate", retired: "Retired" };

export function NoiseLabModels() {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const folderRef = useRef<HTMLInputElement>(null);
  const q = useQuery({
    queryKey: ["lab-models"],
    queryFn: () => api<{ models: LabModel[] }>(`${BASE}/models`),
    enabled: !!me?.is_superadmin,
  });

  async function send(files: File[]) {
    if (!files.length) return;
    setBusy(true);
    setMsg(null);
    try {
      const form = new FormData();
      for (const f of files) form.append("files", f, f.name);
      const m = await upload<LabModel>(`${BASE}/models`, form);
      setMsg({ ok: true, text: `${m.name} added as a candidate.` });
      qc.invalidateQueries({ queryKey: ["lab-models"] });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  if (me && !me.is_superadmin) return <Navigate to="/" replace />;
  const list = q.data?.models ?? [];
  const minutes = (m: LabModel) => (m.training.files ?? []).reduce((a, f) => a + (f.minutes ?? 0), 0);

  return (
    <div className="page admin-page lab-page">
      <LabTabs />
      <div className="page-head">
        <div>
          <h1>Models</h1>
          <div className="sub">
            Every trained version of the noise detector, with how it did on recordings it never heard. Try any version on a
            chapter, then make the best one live: that is the version the detection module will use.
          </div>
        </div>
      </div>

      <div
        className={`dropzone lab-model-drop${drag ? " drag" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); filesFromDrop(e.dataTransfer).then(send); }}
      >
        <div className="lab-drop-title">{busy ? "Adding…" : "Add a model version"}</div>
        <div className="muted lab-drop-hint">Drop the model's folder here: its model.json and the files it names.</div>
        <button type="button" className="btn sm" onClick={() => folderRef.current?.click()} disabled={busy}>Choose folder</button>
        <input ref={folderRef} type="file" hidden multiple {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
               onChange={(e) => { void send(visible(Array.from(e.target.files || []))); e.target.value = ""; }} />
        {msg && <div className={msg.ok ? "lab-ok" : "lab-error"}>{msg.text}</div>}
      </div>

      {q.isLoading ? <div className="muted lab-loading">Loading…</div>
        : q.isError ? <div className="lab-error">Couldn't load the models: {(q.error as Error).message}</div>
        : list.length === 0 ? <div className="empty-state">No model versions yet. Drop a model folder above.</div>
        : (
          <div className="card lab-tablewrap">
            <table className="table lab-table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Status</th>
                  <th className="r" title="How much recording it learned from">Trained on</th>
                  <th className="r" title="Of your mouth-noise edits, how many it found when showing 10 flags a minute (median over the recordings it never heard)">Found at 10 flags/min</th>
                  <th className="r" title="Of the clearer ones (20 dB or more above the noise floor)">Clear ones found</th>
                  <th className="r" title="At the point where it finds half your edits: how many of its flags land on one (a lower bound)">Flags on your edits</th>
                  <th className="r">Trials</th>
                  <th>Trained</th>
                </tr>
              </thead>
              <tbody>
                {list.map((m) => (
                  <tr key={m.id}>
                    <td><Link to={`/admin/noise-lab/models/${m.id}`} className="lab-setlink">{m.name}</Link></td>
                    <td><span className={`lab-pill lab-model-${m.status}`}>{STATUS_LABEL[m.status] ?? m.status}</span></td>
                    <td className="r num">{m.training.files?.length ?? 0} files{minutes(m) ? `, ${Math.round(minutes(m))} min` : ""}</td>
                    <td className="r num">{pct(m.results_summary.recall_at_10_per_min)}</td>
                    <td className="r num">{pct(m.results_summary.clear_edit_recall_at_10_per_min)}</td>
                    <td className="r num">{pct(m.results_summary.precision_at_half_recall)}</td>
                    <td className="r num">{m.n_runs}</td>
                    <td className="muted">{m.created || new Date(m.uploaded_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
